/**
 * Types for the PNOĒ Breath Data API (`/api/v1/*`).
 *
 * Mirrors the service's OpenAPI schemas. Two things to keep in mind:
 *
 * - Every numeric field arrives as a **decimal string** (`"12.5"`), not a
 *   number — the backend serialises Decimal that way. Parse at the boundary
 *   with `toNumber` below rather than sprinkling `Number(...)` through the UI.
 * - Nullable fields are typed `| null` because the API returns explicit nulls
 *   for anything the device never reported.
 */

export type PnoeMeasurementType = "rmr" | "amr";
export type PnoeMeasurementStatus = "in_progress" | "completed" | "invalidated";
export type PnoeReportStatus = "not_started" | "pending" | "ready" | "failed";
export type PnoePhaseType = "resting" | "warmup" | "ramp" | "recovery";
/** `HardwareGeneration` — which PNOĒ device recorded the test. */
export type PnoeDeviceGeneration = "pnoe1" | "pnoe2";

/** Decimal-as-string, e.g. `"73.40"`. */
export type DecimalString = string;

/**
 * The PNOĒ device's identity, as both the listing row and the detail summary
 * carry it since the 13 Oct 2026 contract: a serial number plus the hardware
 * generation. There is no device *name* any more.
 */
export interface PnoeDeviceRef {
  device_pnoe_serial_number: string | null;
  /** Null on historical records, recorded before generations were tracked. */
  device_pnoe_generation: PnoeDeviceGeneration | null;
}

/**
 * `PublicMeasurementListItem` — one row of the listing.
 *
 * Deliberately narrow: the listing returns only these nine fields. Anything
 * else (participant, protocol, HR device, timestamps) comes from the detail
 * call.
 */
export interface PnoeMeasurementListItem extends PnoeDeviceRef {
  id: string;
  customer_id: string | null;
  customer_name: string | null;
  measurement_type: PnoeMeasurementType;
  started_at: string | null;
  status: PnoeMeasurementStatus;
  report_status: PnoeReportStatus;
}

/** `PublicMeasurementsPage` — cursor-paginated listing envelope. */
export interface PnoeMeasurementsPage {
  data: PnoeMeasurementListItem[];
  count?: number | null;
  next_cursor?: string | null;
}

/**
 * `MeasurementSummaryPublic`
 *
 * `report_revision` is optional: the operation responses (complete/invalidate)
 * carry it, but this summary does not.
 */
export interface PnoeMeasurementSummary extends PnoeDeviceRef {
  id: string;
  measurement_type: PnoeMeasurementType;
  status: PnoeMeasurementStatus;
  report_status: PnoeReportStatus;
  started_at: string | null;
  recorded_duration_seconds: DecimalString | null;
  /** The spec types this non-null; historical records may still send "". */
  device_hr_serial_number: string;
  device_hr_brand: string | null;
  report_revision?: number | null;
}

/**
 * One entry of `participant.lifestyle_answers` — the snapshot taken when the
 * answer was given, so its `label`/`question` describe what was shown then,
 * even if the configured wording has changed since.
 */
export interface PnoeLifestyleAnswer {
  value: string;
  label: string;
  question: string;
  details: string;
}

/** Keyed by the question's `field_name`. `{}` on historical measurements. */
export type PnoeLifestyleAnswers = Record<string, PnoeLifestyleAnswer>;

/** `MeasurementParticipantPublic` */
export interface PnoeParticipant {
  company_id: string | null;
  company_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  gender: string | null;
  age: number | null;
  weight_kg: DecimalString | null;
  height_cm: DecimalString | null;
  race: string | null;
  fvc_lt: DecimalString | null;
  /** Legacy single question; null on measurements that answered the lifestyle set instead. */
  exercise_frequency: string | null;
  lifestyle_answers: PnoeLifestyleAnswers;
}

/** `MeasurementProtocolPublic` — AMR fields are null on an RMR test and vice versa. */
export interface PnoeProtocol {
  amr_equipment_type: string | null;
  amr_protocol_level: string | null;
  rmr_last_meal: string | null;
  rmr_last_workout: string | null;
  rmr_last_coffee: string | null;
  rmr_posture: string | null;
}

/**
 * `MeasurementDataPublic` — the detail payload.
 *
 * No device block here: the device identities (PNOĒ serial + generation, HR
 * serial + brand) sit on the summary above. Firmware, mobile app version, gas
 * temperatures and battery level are not available outside the internal
 * namespace.
 */
export interface PnoeMeasurementDetail {
  measurement: PnoeMeasurementSummary;
  participant: PnoeParticipant;
  protocol: PnoeProtocol;
}

/** `MeasurementPhasePublic` */
export interface PnoePhase {
  id: string;
  client_record_id: string | null;
  elapsed_time: DecimalString | null;
  name: string | null;
  type: PnoePhaseType;
  watts: DecimalString | null;
  speed: DecimalString | null;
  grade: DecimalString | null;
}

/** `VO2RecordPublic` — one breath. */
/**
 * One column of the VO₂ series, described by the backend.
 *
 * `/vo2-data` sends this alongside the rows, so which measures exist — and
 * how each is named, united and rounded — is the service's to decide. The
 * chart builds its picker from this rather than from a list of its own, which
 * had already drifted: the columns were renamed (`vo2` → `vo2_ml_min`) and a
 * dozen more added (MET, energy expenditure, substrate shares, ergometer
 * load) while the hardcoded list stood still.
 */
export interface PnoeVo2Field {
  /** The key this column has in every record. */
  key: string;
  /** Full name, e.g. "Oxygen consumption". Null where the API has none. */
  label: string | null;
  /** Short name for a chart legend or column head, e.g. "VO2". */
  display_name: string | null;
  /** Plain-ASCII short name, e.g. "VO2" or "Time". */
  ascii_name: string | null;
  /** Null for a ratio (RER, MET). */
  unit: string | null;
  /** Decimal places to display; the values themselves are full precision. */
  display_decimal_places: number | null;
}

/**
 * One breath. Beyond the three fixed columns the measures are dynamic — read
 * them by `PnoeVo2Field.key` rather than by a declared property.
 */
export interface PnoeVo2Record {
  id: string;
  client_record_id: string | null;
  elapsed_time: DecimalString | null;
  [field: string]: DecimalString | null;
}

/** One page of `/vo2-data`: the column metadata plus the rows. */
export interface PnoeVo2Page {
  fields: PnoeVo2Field[];
  data: PnoeVo2Record[];
  next_cursor: string | null;
}

/** `HRSamplePublic` */
export interface PnoeHrSample {
  id: string;
  client_record_id: string | null;
  elapsed_time: DecimalString | null;
  rr: DecimalString | null;
  hr: DecimalString | null;
}

/** `MeasurementEventPublic` */
export interface PnoeMeasurementEvent {
  id: string;
  client_record_id: string | null;
  elapsed_time: DecimalString | null;
  source: "device" | "mobapp" | "server";
  event_type: string | null;
  description: string | null;
}

/** Every series endpoint shares this cursor envelope. */
export interface PnoeCursorPage<T> {
  data: T[];
  next_cursor?: string | null;
}

/** `MeasurementCatalogsPublic` — display labels for the enums. */
export interface PnoeCatalogOption<T extends string> {
  value: T;
  label: string;
}

export interface PnoeMeasurementCatalogs {
  measurement_types: PnoeCatalogOption<PnoeMeasurementType>[];
  measurement_statuses: PnoeCatalogOption<PnoeMeasurementStatus>[];
  report_statuses: PnoeCatalogOption<PnoeReportStatus>[];
  phase_types: PnoeCatalogOption<PnoePhaseType>[];
  /** Absent from a payload cached before the 13 Oct 2026 contract. */
  device_pnoe_generations?: PnoeCatalogOption<PnoeDeviceGeneration>[];
}

/**
 * One choice of a participant / protocol / lifestyle question, from the single
 * `GET /configuration` payload (`PnoeBreathConfiguration`).
 */
export interface PnoeParameterOption {
  value: string;
  label: string;
  /**
   * False = no longer offered. A stored answer may still point at such a
   * choice, so display code keeps resolving its label; only the edit selects
   * drop it. Absent (= enabled) on the exercise (AMR) options.
   */
  is_enabled?: boolean;
  /** Only present on the exercise (AMR) options. */
  key?: string;
}

/** `ParticipantParameterPublic` / `RestingProtocolParameterPublic`. */
export interface PnoeConfigParameter {
  id?: string;
  field_name: string;
  question: string;
  /** Help text for the question; "" when there is none. */
  details?: string;
  answers: PnoeParameterOption[];
  sort_order?: number | null;
  is_active?: boolean;
  updated_at?: string;
}

/** One row of `configuration.lifestyle_parameters`. */
export interface PnoeLifestyleParameter {
  field_name: string;
  question: string;
  details: string;
  answers: PnoeParameterOption[];
  sort_order: number;
}

/**
 * `MeasurementConfigurationPublic` — the `GET /configuration` envelope.
 *
 * The protocol variations and exercise parameters are not consumed by the
 * frontend, so they stay untyped.
 */
export interface PnoeBreathConfiguration {
  catalogs: PnoeMeasurementCatalogs;
  participant_parameters: PnoeConfigParameter[];
  resting_protocol_parameters: PnoeConfigParameter[];
  /** Absent from a payload cached before the 13 Oct 2026 contract. */
  lifestyle_parameters?: PnoeLifestyleParameter[];
  exercise_protocol_parameters?: unknown[];
  resting_protocol_variations?: unknown[];
  exercise_protocol_variations?: unknown[];
}

/** `MeasurementOperationPublic` — what complete/invalidate/PATCH return. */
export interface PnoeMeasurementOperation {
  measurement_id: string;
  status: PnoeMeasurementStatus;
  report_status: PnoeReportStatus;
  report_revision: number | null;
}

/** `ParticipantUpdate` — every field optional; only send what changed. */
export interface PnoeParticipantUpdate {
  customer_name?: string;
  gender?: string;
  age?: number;
  weight_kg?: DecimalString;
  height_cm?: DecimalString;
  race?: string;
  fvc_lt?: DecimalString;
  exercise_frequency?: string;
  /**
   * Partial map of question key → choice value. Only supported, enabled
   * choices are accepted, and a change recalculates the report.
   */
  lifestyle_answers?: Record<string, string> | null;
}

/** `ProtocolUpdate` — RMR parameters only; the AMR ones are not editable. */
export interface PnoeProtocolUpdate {
  rmr_last_meal?: string;
  rmr_last_workout?: string;
  rmr_last_coffee?: string;
  rmr_posture?: string;
}

/**
 * `PhaseUpdate` — the four correctable fields of a phase. The name and type are
 * immutable and are refused by the endpoint. `null` clears a load value.
 */
export interface PnoePhaseUpdate {
  elapsed_time?: DecimalString | null;
  watts?: DecimalString | null;
  speed?: DecimalString | null;
  grade?: DecimalString | null;
}

/**
 * `PhaseBatchUpdate` — phase id → correction, 1–1000 entries.
 *
 * Applied atomically: one invalid entry rejects the whole batch, so the phases
 * are never left half-moved.
 */
export type PnoePhaseBatchUpdate = Record<string, PnoePhaseUpdate>;

/**
 * Query params accepted by `GET /api/v1/measurements/`.
 *
 * There is no `company` param — the service scopes results to the bearer's
 * company — and no `sort`/`direction`: the order is always `started_at` DESC.
 */
export interface PnoeMeasurementsQuery {
  measurement_id?: string;
  customer_id?: string;
  customer_name?: string;
  device_pnoe_serial_number?: string;
  measurement_type?: PnoeMeasurementType;
  status?: PnoeMeasurementStatus;
  report_status?: PnoeReportStatus;
  started_from?: string;
  started_to?: string;
  limit?: number;
  cursor?: string;
  include_total?: boolean;
}

/**
 * Decimal strings → numbers, tolerating null/""/garbage. Returns `null` rather
 * than NaN so callers can render an em dash instead of "NaN".
 */
export const toNumber = (value: DecimalString | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The inverse of `toNumber`, for a PATCH body.
 *
 * Every decimal on this API is a `DecimalString` in **both** directions: a
 * JSON number is rejected outright —
 * `{"type":"value_error","msg":"Value error, decimal values must be JSON
 * strings"}` — so a computed value has to be serialised before it is sent.
 *
 * `decimals` also strips float noise: converting 140 lb to kg yields
 * `63.502931800000006`, and sending 13 decimal places of a bathroom-scale
 * reading is meaningless as well as ugly. Trailing zeros go, so a whole number
 * sends as `"64"` rather than `"64.000"`.
 */
export const toDecimalString = (
  value: number | string | null | undefined,
  decimals = 3
): DecimalString | null => {
  const numeric = toNumber(value as any);
  if (numeric === null) return null;
  return String(Number(numeric.toFixed(decimals)));
};
